// StoresPopup.tsx - Small popup to show stores list
import React, { useRef, useEffect } from "react";

interface Store {
  name: string;
  state: string;
}

interface StoresPopupProps {
  stores: Store[];
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement>;
}

const StoresPopup: React.FC<StoresPopupProps> = ({
  stores,
  isOpen,
  onClose,
  anchorRef,
}) => {
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: "fixed" as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 999,
      backgroundColor: "transparent",
    },
    popup: {
      position: "absolute" as const,
      top: "calc(100% + 8px)",
      right: 0,
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      border: "1px solid #e5e7eb",
      minWidth: "280px",
      maxWidth: "350px",
      maxHeight: "300px",
      overflowY: "auto" as const,
      zIndex: 1000,
      animation: "fadeIn 0.2s ease-out",
    },
    header: {
      padding: "12px 16px",
      borderBottom: "1px solid #e5e7eb",
      fontWeight: 600,
      fontSize: "14px",
      color: "#111827",
      position: "sticky" as const,
      top: 0,
      backgroundColor: "white",
      zIndex: 1,
    },
    storesList: {
      padding: "8px 0",
    },
    storeItem: {
      padding: "10px 16px",
      fontSize: "13px",
      color: "#374151",
      borderBottom: "1px solid #f3f4f6",
      transition: "background-color 0.2s",
    },
    storeItemHover: {
      backgroundColor: "#f9fafb",
    },
    storeName: {
      fontWeight: 500,
      color: "#111827",
      marginBottom: "4px",
    },
    storeState: {
      fontSize: "12px",
      color: "#6b7280",
    },
    emptyState: {
      padding: "20px 16px",
      textAlign: "center" as const,
      color: "#9ca3af",
      fontSize: "13px",
    },
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-4px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <div style={styles.overlay} onClick={onClose} />
      <div ref={popupRef} style={styles.popup}>
        <div style={styles.header}>
          Stores ({stores.length})
        </div>
        {stores.length > 0 ? (
          <div style={styles.storesList}>
            {stores.map((store, index) => (
              <div
                key={index}
                style={styles.storeItem}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <div style={styles.storeName}>{store.name}</div>
                <div style={styles.storeState}>{store.state}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={styles.emptyState}>No stores available</div>
        )}
      </div>
    </>
  );
};

export default StoresPopup;

// ==================== Updated AnnouncementItem Component ====================
/*
// Add this import at the top:
import StoresPopup from "./StoresPopup";

// Inside AnnouncementItem component, add these state and ref:
const [showStoresPopup, setShowStoresPopup] = useState(false);
const funnelIconRef = useRef<HTMLDivElement>(null);

// Replace the Funnel icon div with this:
<div 
  ref={funnelIconRef}
  style={{ cursor: "pointer", position: "relative" }}
  onClick={() => setShowStoresPopup(!showStoresPopup)}
>
  <Funnel height={16} />
  <StoresPopup
    stores={announcement.stores || []}
    isOpen={showStoresPopup}
    onClose={() => setShowStoresPopup(false)}
    anchorRef={funnelIconRef}
  />
</div>
*/